// SettingsAiPane.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Services
import {
  getBaseAiSettings,
  updateBaseAiSettings,
} from 'services/bases/BaseAiSettingsService';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * SettingsAiPane
 *
 * Lets workbase owners/admins choose the default Ai model.
 *
 * @param {object} base - Active base model.
 *
 * @returns {ReactNode} The rendered AI settings pane.
 */
const SettingsAiPane = React.memo(({
  base,
}) => {
  const baseKey = useMemo(
    () => String(base?.key || '').trim(),
    [base?.key]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');

  const loadSettings = useCallback(async () => {
    if (!baseKey) {
      setProfiles([]);
      setSelectedProfileId('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const payload = await getBaseAiSettings(baseKey);
      const nextProfiles = Array.isArray(payload?.profiles) ? payload.profiles : [];
      const nextSelected = String(payload?.defaultPrimaryProfileId || '').trim()
        || String(nextProfiles[0]?.profileId || '').trim();

      setProfiles(nextProfiles);
      setSelectedProfileId(nextSelected);
    } catch (error) {
      console.error('[SettingsAiPane] Failed to load AI settings:', error);
      setErrorMessage(error?.message || 'Unable to load AI settings.');
      setProfiles([]);
      setSelectedProfileId('');
    } finally {
      setLoading(false);
    }
  }, [baseKey]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleModelChange = async (event) => {
    const nextProfileId = String(event?.target?.value || '').trim();
    if (!baseKey || !nextProfileId || nextProfileId === selectedProfileId || saving) {
      return;
    }

    const previousProfileId = selectedProfileId;
    setSelectedProfileId(nextProfileId);
    setSaving(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const payload = await updateBaseAiSettings({
        baseId: baseKey,
        defaultPrimaryProfileId: nextProfileId,
      });
      const nextProfiles = Array.isArray(payload?.profiles) ? payload.profiles : profiles;
      setProfiles(nextProfiles);
      setSelectedProfileId(
        String(payload?.defaultPrimaryProfileId || nextProfileId).trim()
      );
      setStatusMessage('Model saved.');
    } catch (error) {
      console.error('[SettingsAiPane] Failed to save AI settings:', error);
      setSelectedProfileId(previousProfileId);
      setErrorMessage(error?.message || 'Unable to save AI settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsModalPaneHeader
        title="AI Model"
        subtitle="Choose the default model for this base."
      />

      <SettingsModalPaneContents>
        {errorMessage ? (
          <div className="settings-modal-error">{errorMessage}</div>
        ) : null}

        {statusMessage && !errorMessage ? (
          <div className="settings-modal-status">{statusMessage}</div>
        ) : null}

        <div style={{ maxWidth: 420 }}>
          <SelectInput
            value={selectedProfileId}
            onChange={handleModelChange}
            disabled={loading || saving || profiles.length === 0}
            endIconKey="keyboard_arrow_down"
            style={{
              fontSize: '14px',
            }}
            placeholder={loading ? 'Loading models…' : 'No models available'}
            options={profiles.map((profile) => ({
              value: profile.profileId,
              label: profile.label || profile.profileId,
            }))}
          />
        </div>
      </SettingsModalPaneContents>
    </>
  );
});

SettingsAiPane.displayName = 'SettingsAiPane';

export default SettingsAiPane;
